import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { Title } from '../titles/Title';
import { Button } from '../buttons/Button';
import { LayoutCard } from './LayoutCard';

type Props = {
  title?: string,
  content?: Function,
  handlePrimaryClick: Function,
  handleSecondaryClick?: Function,
  primaryButton?: string,
  secondaryButton?: string,
  editButton?: string,
  footerAlign?: 'left' | 'right',
  children?: React.Node,
};

export const EditSection = ({
  title,
  content,
  handlePrimaryClick,
  handleSecondaryClick,
  primaryButton,
  secondaryButton,
  editButton,
  footerAlign,
  children,
  ...rest
}: Props) => {
  const { t } = useTranslation('');

  const [editMode, toggleEditMode] = useState(false);

  const onPrimaryButtonClick = useCallback(() => {
    handlePrimaryClick();
    toggleEditMode(false);
  }, [handlePrimaryClick]);

  const onSecondaryButtonClick = useCallback(() => {
    handleSecondaryClick && handleSecondaryClick();
    toggleEditMode(false);
  }, [handleSecondaryClick]);

  return (
    <LayoutCard {...rest}>
      <Header>
        {title && (
          <Title level={2} py={2} margin="none">
            {title}
          </Title>
        )}
        {!editMode && (
          <Button ml="auto" size="small" mode="light" onClick={() => toggleEditMode(true)}>
            {editButton || t('common:edit')}
          </Button>
        )}
      </Header>
      {content && content({ isDisabled: !editMode })}
      {children}
      {editMode && (
        <Footer footerAlign={footerAlign}>
          <Button size="small" onClick={onPrimaryButtonClick}>
            {primaryButton || t('common:save')}
          </Button>
          <Button size="small" mode="light" onClick={onSecondaryButtonClick}>
            {secondaryButton || t('common:cancel')}
          </Button>
        </Footer>
      )}
    </LayoutCard>
  );
};

const Header = styled('header')`
  display: flex;
  align-items: center;
  margin: -10px 0 14px;
`;

const Footer = styled('footer')`
  margin-top: 24px;
  display: grid;
  grid-gap: 16px;
  grid-auto-flow: column;
  grid-template-columns: max-content;
  justify-content: ${({ footerAlign }) => (footerAlign === 'right' ? 'end' : 'start')};

  button {
    justify-self: start;
  }
`;
